import React, { useState, useEffect, useCallback } from 'react';
import { Heart, ShieldCheck, Target, Users, ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { HERO_FEATURES, IMAGES } from '../mock';
import { getToken } from '../lib/auth';

const ICONS = { Heart, ShieldCheck, Target, Users };

const FLOATING = [
  { left: '6%', size: 22, delay: 0, dur: 9, cls: '' },
  { left: '14%', size: 14, delay: 2.5, dur: 11, cls: 'soft' },
  { left: '30%', size: 18, delay: 1, dur: 10, cls: 'pink' },
  { left: '44%', size: 12, delay: 3.5, dur: 12, cls: 'soft' },
  { left: '52%', size: 24, delay: 0.8, dur: 8.5, cls: '' },
  { left: '66%', size: 16, delay: 2, dur: 11, cls: 'pink' },
  { left: '78%', size: 20, delay: 1.5, dur: 9.5, cls: '' },
  { left: '88%', size: 13, delay: 3, dur: 12.5, cls: 'soft' },
  { left: '95%', size: 18, delay: 0.4, dur: 10.5, cls: 'pink' },
];

const SLIDES = IMAGES.heroSlider;

const Hero = () => {
  const [idx, setIdx] = useState(0);
  const loggedIn = !!getToken();

  const next = useCallback(() => setIdx((i) => (i + 1) % SLIDES.length), []);
  const prev = () => setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length);

  useEffect(() => {
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, [next, idx]);

  return (
    <section className={`hero${loggedIn ? ' hero-logged-in' : ''}`} id="home">
      <div className="hearts">
        {FLOATING.map((h, i) => (
          <Heart
            key={i}
            className={`fheart ${h.cls}`}
            size={h.size}
            fill="currentColor"
            style={{ left: h.left, bottom: '8%', animationDelay: `${h.delay}s`, animationDuration: `${h.dur}s` }}
          />
        ))}
      </div>
      <div className="container hero-grid">
        <div className="hero-copy">
          <div className="badge">
            <Heart size={14} fill="#fff" /> NO SWIPING. NO STRESS. JUST YOUR VIBE.
          </div>
          <h1>Find Your Perfect Match<br /><span>With Your Vibe</span></h1>
          <p>Tell us about yourself and your preferences. We will find the most compatible matches for you.</p>

          <div className="feature-strip">
            {HERO_FEATURES.map((f) => {
              const Ic = ICONS[f.icon];
              return (
                <div className="feature" key={f.title}>
                  <div className="fi"><Ic size={24} /></div>
                  <b>{f.title}</b>
                  <span>{f.desc}</span>
                </div>
              );
            })}
          </div>

          <div className="hero-buttons" style={{ display: 'flex', gap: 12, marginTop: 26 }}>
            <a className="btn btn-red" href="#match" data-testid="hero-find-match-btn">Find My Match <ArrowRight size={17} /></a>
            <a className="btn btn-ghost" href="#how" data-testid="hero-how-it-works-btn">See How It Works</a>
          </div>
          <p className="cta-note" data-testid="hero-cta-note">
            <Heart size={12} fill="currentColor" /> Fill your matchmaking form — we'll find the right match for your vibe.
          </p>
        </div>

        <div className="hero-visual">
          <div className="slider" data-testid="hero-slider">
            {SLIDES.map((src, i) => (
              <div key={i} role="img" aria-label={`Happy couple matched through Krysha — photo ${i + 1}`} className={`slide ${i === idx ? 'show' : ''}`} style={{ backgroundImage: `url('${src}')` }} />
            ))}
            <button className="sl-arrow left" aria-label="Previous photo" data-testid="hero-slider-prev" onClick={prev}><ChevronLeft size={20} /></button>
            <button className="sl-arrow right" aria-label="Next photo" data-testid="hero-slider-next" onClick={next}><ChevronRight size={20} /></button>
            <div className="sl-dots">
              {SLIDES.map((_, i) => (
                <button key={i} aria-label={`Photo ${i + 1}`} className={`sl-dot ${i === idx ? 'on' : ''}`} data-testid={`hero-slider-dot-${i}`} onClick={() => setIdx(i)} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
